/**
 * Components and helpers for groups/interventions
 * @module
 */
import * as _ from 'lodash'
import { A, P, all, and, Div, not, or, TBody, equal, Variable, VArray, VBoolean, Button, ReplacedEvent } from 'alkali'
import { TR, TH, TD, Span, I } from 'alkali'
import { liveParsed } from 'model/SearchQuery'
import Icon from 'components/Icon'
import Dropdown from 'components/generic/Dropdown'
import { DAStudyGroup, EGroup, ERow } from 'services/extract/types'
import { validResult } from './model/validation'
import {
	createExtractGroup,
	rowToStr,
	validItvnName,
	VGroup, VGroupN, VIntervention, VRow
} from 'components/extract/model/extraction'
import { DosageExtractField, ExtractField, PhraseExtractField } from 'components/extract/Inputs'
import * as deleteIcon from 'icons/delete-1.svg'
import * as addEntry from 'icons/multiple-actions-add.svg'
import * as uneditableEntry from 'icons/multiple-actions-block.svg'
import * as multipleUsers from 'icons/multiple-neutral-2.svg'
import * as arrowDown from 'icons/selectivity-caret.svg'
import * as pluralize from 'pluralize'
import { FocusedState } from 'components/extract/ExtractAnnotationModel'
import { EntityComparison, ExtractionComparison, detectInvalidNewGroups } from 'components/extract/model/comparison'
import { createExtractItvn, toExtractGroup } from 'services/extract/generate'
import { groupToStr as egroupToStr } from 'services/extract/util'
import StudyContext from 'components/extract/model/StudyContext'
import { Chat } from 'integrations/intercom'
import { ParsedQuery } from 'util/terms'
import { CTG_SOURCE, EU_SOURCE } from 'components/studies/studyUrls'
import { BaseExtractionModel } from 'components/extract/model/ExtractionModel'
const editGroup = multipleUsers

const groupToName = g => g.Name || g.OriginalName
const groupToStr = g => groupToName(g) + (g.N ? ` (${g.N})` : '')

/**
 * State/parameters structure threaded through various component invocations
 */
export interface GroupsContext {
	studyCtx: StudyContext
	groups: VArray<VGroup>
	outcome: VRow
	addValidation?: Variable
	canAddGroup?: VBoolean
	canDeleteGroup?: VBoolean
	invalidNewGroups?: Variable<EGroup[]>
	focusedState: FocusedState
}

/**
 * Mode/context the component is being used in
 */
export enum Mode {
	/**
	 * Exclusively editing an existing entity
	 */
	EDIT = 'edit',
	/**
	 * Adding a new entity
	 * Values used to match/select an existing entity
	 */
	ADD = 'add',
	/**
	 * Editing a prior cell extraction.
	 * Row/Groups/Interventions not generally editable
	 */
	EXTRACT_EDIT = 'extract-edit',
	/**
	 * Adding a new cell extraction
	 * Rows/Groups can be entered for selection, or addition
	 */
	EXTRACT_ADD = 'extract-add'
}

/**
 * Calculate various state flags based on mode and entity operated on, and existing alternates
 */
async function compState(mode: Mode, group: EGroup, groups: EGroup[], invalidGroups: EGroup[] = [], studyCtx: StudyContext) {
	let valid
	let comparison: EntityComparison<any, DAStudyGroup>
	let configurable = true
	let invalidAddition = false
	if (group.existing) {
		comparison = studyCtx.evalGroup(group.existing.id)
		configurable = !!comparison.editable
		if (mode == Mode.EDIT) {
			// allow editing RE and CTG groups
			if (configurable && CTG_SOURCE.sources.includes(studyCtx.study.source) == false
							 && EU_SOURCE.sources.includes(studyCtx.study.source) == false) {
				configurable = !!comparison.matches.find(m => m.isEditable && m.isRE)
			}
			valid = configurable
		} else if (mode == Mode.EXTRACT_ADD || mode == Mode.EXTRACT_EDIT) {
			// should be pre-filtering referenceable entities anyway
			valid = comparison.matches.every(m => m.isReferenceable)
			// for now do not allow editing in cell extract mode
			configurable = false
		} else {
			// cannot add an existing group; invalid state
			valid = false
			configurable = false
		}
	} else {
		comparison = await studyCtx.compareGroup(group)
		if (mode == Mode.ADD) {
			// if we are adding a new entry, there can't be any existing matches
			const isnew = !comparison
			invalidAddition = isnew && invalidGroups.some(g => g.id == group.id)
			valid = isnew && !invalidAddition
		} else if (mode == Mode.EXTRACT_ADD) {
			// should be pre-filtering referenceable entities anyway
			const isnew = !comparison
			invalidAddition = isnew && invalidGroups.some(g => g.id == group.id)
			valid = isnew ? !invalidAddition : comparison.matches.every(m => m.isReferenceable)
		} else {
			// cannot edit a non-existent group
			valid = false
			configurable = false
		}
	}
	const otherGroups = groups.filter(g => g != group)
	const availableGroups = studyCtx.groups.filter(studyGroup => studyGroup.Interventions?.length &&
		studyCtx.evalGroup(studyGroup.Id)?.matches?.some(m => m.isReferenceable) &&
		!otherGroups.find(g => g.id == studyGroup.Id)
	)
	return {
		mode,
		valid,
		invalidAddition,
		configurable,
		comparison,
		availableGroups
	}
}

const CaretIcon = Icon('.caret', { icon: arrowDown })

class GroupStatus extends Div('.group-status') {
	created(props) {
		const { group, state } = props
		const title = state.to(state => {
			const { mode, valid, invalidAddition, comparison: { matches = [] } = {} } = state
			const detail = matches?.length ? 'Existing groups: ' + matches.map(m => groupToStr(m.match)).join('; ') : 'New Group'
			let title = ''
			if (!valid || invalidAddition) {
				title += 'Cannot ' + mode.split('-')[0] + ' group'
				if (mode == Mode.EXTRACT_ADD && invalidAddition) {
					title += ': ' + BaseExtractionModel.GROUP_ADD_DISALLOWED
				}
				title += '\n'
			}
			title += detail
			return title
		})
		const icon = state.to((state = {}) => {
			const { mode, comparison: { matches = [], uneditable } = {} } = state
			return (mode == Mode.EDIT && uneditable) ? uneditableEntry : matches?.length ? editGroup : addEntry
		})

		const isSelect = equal(state.property('mode'), Mode.EXTRACT_ADD)
		const addValid = or(not(isSelect), not(state.property('invalidAddition')))
		const stateValid = state.property('valid')
		const err = or(not(stateValid), not(addValid))
		let dropdown
		const selectEnabled = or(state.property('availableGroups').property('length'), group.existing)
		const canSelect = and(isSelect, selectEnabled)

		const StatusIcon = Icon({
			classes: {
				'err-icon': err
			},
			icon,
			title
		})
		props.classes = {
			'status-icon': not(isSelect)
		}
		props.content = isSelect.to(isSelect => [
			isSelect ?
				Button('.inline-input-btn.center-icon-btn.icons-btn', {
					title,
					disabled: not(selectEnabled),
					onclick: (event) => {
						event.preventDefault()
						if (canSelect.valueOf()) {
							dropdown || (dropdown = new Dropdown({
								items: all([state.property('availableGroups'), group.existing], (groups, current) => groups.map(g => ({
									content: [
										Span({ title: groupToStr(g) }, groupToStr(g))
									],
									onclick() {
										const event = new ReplacedEvent()
										event.reassigning = true
										group.put(createExtractGroup(toExtractGroup(g)), event)
									}
								})).concat(current ? [{
									content: [
										Span({ title: 'Clear selected group' }, 'Clear selected group')
									],
									onclick() {
										group.put(createExtractGroup())
									}
								}] : []))
							}))
							dropdown.show(event)
						}
					}
				}, [StatusIcon, CaretIcon])
				: StatusIcon
		])
	}
}

/**
 * Header for the logical group row, spans the intervention rows
 */
export const GroupRowHeader = ({ groupsContext, group, itvnCount, state }) =>
	TH('.group-row-hdr', { attributes: { scope: 'row', rowspan: itvnCount.to(i => i + 1) } },
		GroupRowHeaderContent({ groupsContext, group, state })
	)

/**
 * Group header content, contains the GroupStatus selection icon/dropdown and optionally deletion button
 */
export const GroupRowHeaderContent = ({ groupsContext: { groups, addValidation, canDeleteGroup }, group, state }) =>
	Div('.group-row-hdr-content', [
		new GroupStatus({ group, state, addValidation }),
		// can't use transform here or icon gets duplicated each time groups is updated
		Icon('.delete-group.clickable', {
			title: 'Delete Group',
			classes: {
				// we can only delete the group entry/component if we are extracting in add/select mode
				// and # of groups conforms to analysis
				hidden: not(and(equal(state.property('mode'), Mode.EXTRACT_ADD), canDeleteGroup))
			},
			icon: deleteIcon,
			onclick() {
				groups.splice(groups.indexOf(group), 1)
			}
		})
	])

export const GroupNCell = ({ groupsContext: { outcome, focusedState }, group, itvnCount }) =>
	TD('.group-n-cell', { attributes: { rowspan: itvnCount } }, [
		GroupNField({
			focusedState,
			obj: outcome ? outcome.cells.property(group.id.valueOf()).property('n').as(VGroupN) : group.n
		})
	])

interface ItvnFieldsConfig {
	/** The TDs to render for the intervention */
	cells: any[]
	/** Any trailing TDs to render for this Itvn row - i.e. the Cell inputs */
	trailing?: any[]
}

/**
 * Generates the cell field configuration for an intervention
 */
export function interventionCells(intervention: VIntervention, interventions, focusedState, hideOp = false, state): ItvnFieldsConfig {
	// support just one treatment for now
	const treatment = intervention.treatments.property(0)
	const configurable = state.property('configurable')
	const editableShown = { classes: { hidden: not(configurable) } }
	const roShown = { classes: { hidden: configurable } }
	return {
		cells: [
			TD('.itvn-name-cell', [
				Span('.ro-itvn-name', roShown, intervention.name.val),
				new PhraseExtractField('.treatment-term', {
					ftype: 'intervention',
					placeholder: 'treatment name',
					disabled: not(configurable),
					obj: intervention.name,
					focusedState,
					allowFreeText: true,
					freeTextWarning: 'This intervention does not match an existing record in our ontology. Please double check spelling before proceeding.',
					...editableShown
				})
			]),
			TD('.dosage-cell', [
				Span(roShown, treatment.measure.val),
				new DosageExtractField('.dosage', {
					ftype: 'dosage',
					placeholder: 'dosage',
					disabled: not(configurable),
					obj: treatment.measure,
					optional: true,
					focusedState,
					...editableShown
				})
			]),
			TD('.row-op-cell', { classes: { hidden: hideOp } }, Icon('.delete-intervention.clickable', {
				title: 'Delete Intervention',
				classes: {
					invisible: or(interventions.to(itvns => itvns.length < 2), not(configurable))
				},
				icon: deleteIcon,
				onclick() {
					interventions.splice(interventions.indexOf(intervention), 1)
					interventions.updated()
				}
			}))
		]
	}
}

/**
 * Use to render just the group name instead of individual interventions, under certain conditions
 */
export const readOnlyGroupHeaderRow = ({ groupsContext, group, inputCells, hideOp, state }) =>
	InterventionRow({
		groupsContext,
		group,
		itvnConfig: {
			cells: [
				// render the full group name
				TD('.group-name-cell', Span('.ro-group-name', group.to(group => {
					const { studyCtx: { study: { groups } } } = groupsContext
					const existing = groups.find(g => g.Id == group.existing?.id)
					return existing ? groupToName(existing) : I('<no group name>')
				}))),
				TD('.dosage-cell'),
				TD('.row-op-cell', { classes: { hidden: hideOp } })
			],
			trailing: inputCells
		},
		first: new Variable(true),
		itvnCount: group.interventions.to(itvns => itvns.length + 1 /* plus extra group header */),
		state
	})

const COMBO_SEPARATOR = /\w+\s*[+,/]\s*\w+/

/**
 * Returns group-related error content
 */
export const groupStatusMsg = (group, groups, row, studyCtx: StudyContext, comparison: Variable<ExtractionComparison>) => all([group, groups, row, comparison], (group: EGroup, groups: EGroup[], row: ERow, { comparisons, groupMap }: ExtractionComparison) => {
	const errs = []
	const invalidItvns = (group.interventions || []).map(itvn => validItvnName(itvn.name, itvn.existing)).filter(result => !result.isValid)
	if (invalidItvns.length) {
		errs.push(..._.uniq(invalidItvns.flat()))
	}
	const comparison = comparisons.find(c => ((c.group == null && group == null) || c.group?.id == group.id) && c.row.id == row.id)
	if (comparison?.uneditable) {
		errs.push(`Cannot edit ${rowToStr(row)} value` + (comparison.matchingGroups?.length ?
			` for existing extracted ${pluralize('group', comparison.matchingGroups.length)}: ${comparison.matchingGroups.map(groupToStr)}` : ''))
	}
	return errs
})

export const groupStatusMsgOnly = (group) => all([group], (group: EGroup) => {
	const errs = []
	const invalidItvns = (group.interventions || []).map(itvn => validItvnName(itvn.name, null)).filter(result => !result.isValid)
	if (invalidItvns.length) {
		errs.push(..._.uniq(invalidItvns.flat()))
	}
	return errs
})

/**
 * Generates content for the group "operations" row, including group status messages if any
 */
export function interventionOpCells(group, colspan: Variable<number> | number = 1, state, statusMsgs?) {
	return [
		TD('.op-cell', { attributes: { colspan } }, Div('.op-cell-container', [
			statusMsgs ? Div('.group-note', { classes: { hidden: not(statusMsgs.to(msgs => msgs.length)) } }, statusMsgs.to(msgs =>
				msgs.map(m => P('.group-err', m))
			)) : undefined,
			A('.op-link', {
				title: 'Click here to add additional treatment to group',
				classes: {
					hidden: not(state.property('configurable'))
				},
				onclick(e) {
					group.interventions.push(createExtractItvn())
					return false
				}
			}, 'Add intervention')

		]))
	]
}

const GroupNField = ({focusedState, obj}) =>
	new ExtractField('.group-n', {
		ftype: 'group-n',
		obj,
		focusedState,
		title: obj.val
	})

/**
 * Generates an intervention row; IFF this is the first intervention row, we include the GroupRowHeader and Group N field
 */
const InterventionRow = ({ groupsContext, itvnConfig: { cells, trailing = []}, first, group, itvnCount, state }: any & { itvnConfig: Itvn }, showGroupN = true) => {
	const { outcome, focusedState, } = groupsContext
	return TR('.itvn-row', { classes: { 'read-only': not(state.property('configurable')) } }, first.to(first => [
		first ? GroupRowHeader({ groupsContext, group, itvnCount, state }) : undefined,
		...cells,
		(first && showGroupN) ? GroupNCell({ groupsContext, group, itvnCount }) : undefined,
		...trailing
	]))
}

/**
 * Generates the reactive group state
 */
export const groupState = ( mode, groupsContext, group) =>
	all([mode, groupsContext.groups, groupsContext.invalidNewGroups, group], (mode, groups, invalidGroups, resolvedGroup) =>
		compState(mode, resolvedGroup, groups, invalidGroups, groupsContext.studyCtx))

/**
 * Renders group rows. Maps each intervention to an InterventionRow
 */
export function groupRows({ groupsContext, group, itvnCells, leadingRow = new Variable() }, state, showGroupN = true) {
	const itvnCount = group.interventions.to(itvns => itvns.length)
	return itvnCells.map(itvnConfig => {
		const first = leadingRow.to(l => !l && itvnCells.indexOf(itvnConfig) == 0)
		return InterventionRow({
			groupsContext,
			group, itvnConfig, itvnCount, state, first
		}, showGroupN)
	})
}

/**
 * Trailing row with 'Add group' link, dependent upon the ability to add groups
 * @see BaseExtractionModel.addValidation
 */
export const AddGroupRow = ({ groups, canAddGroup }: GroupsContext) =>
	TBody('.add-group-row', {
		classes: {
			hidden: not(canAddGroup)
		}
	}, [TR(TD('.op-cell', { attributes: { colspan: 3  } }, A('.op-link', {
		onclick() {
			groups.push(createExtractGroup())
			return false
		}
	}, 'Add group')))])